import PropTypes from 'prop-types';

import Select from '#globalComponents/Forms/Select';
import HeaderFilter from '#globalComponents/Table/filters/HeaderFilter';

import { VOCABULARY_FILTER_LISTS } from '../../../constants/leadFilters';
import { vocabularyOptions } from '../../../utils/targetingLabels';

const YES_NO_OPTIONS = [
	{ value: 'true', label: 'Ja' },
	{ value: 'false', label: 'Nein' },
];

const PROFILE_FILTER_KEYS = ['hasWebsiteProfile', 'customerType', 'businessModel', 'hiringActive', 'oldestCeoAge'];

const toSelectValue = (value) => (value === undefined ? '' : String(value));

const filterOptions = (vocabulary, paramKey) => vocabularyOptions(vocabulary?.[VOCABULARY_FILTER_LISTS[paramKey]]);

const fromSelectValue = (raw, isBoolean) => {
	if (raw === '') return undefined;
	return isBoolean ? raw === 'true' : raw;
};

// One filter param as a select with "Alle"; the backend filters take a single value each.
const FilterSelect = ({ heading, paramKey, options, value, isBoolean = false, onChange }) => (
	<div>
		<label htmlFor={`hf-${paramKey}`} className="block text-xs text-bgBase-800 mb-1">
			{heading}
		</label>
		<Select
			id={`hf-${paramKey}`}
			value={toSelectValue(value)}
			onChange={(event) => onChange({ [paramKey]: fromSelectValue(event.target.value, isBoolean) })}
		>
			<option value="">Alle</option>
			{options.map((option) => (
				<option key={option.value} value={option.value} disabled={option.disabled} title={option.title}>
					{option.label}
				</option>
			))}
		</Select>
	</div>
);

FilterSelect.propTypes = {
	heading: PropTypes.string.isRequired,
	paramKey: PropTypes.string.isRequired,
	options: PropTypes.arrayOf(
		PropTypes.shape({
			value: PropTypes.string,
			label: PropTypes.string,
			disabled: PropTypes.bool,
			title: PropTypes.string,
		}),
	).isRequired,
	value: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
	isBoolean: PropTypes.bool,
	onChange: PropTypes.func.isRequired,
};

/** Website-profile and managing-director filters of the "Profil" column. */
export const ProfileFilterControl = ({ filters, onChange, vocabulary }) => (
	<HeaderFilter label="Nach „Profil“ filtern" active={PROFILE_FILTER_KEYS.some((key) => filters[key] !== undefined)}>
		<div className="space-y-2">
			<FilterSelect
				heading="Website-Profil vorhanden"
				paramKey="hasWebsiteProfile"
				options={YES_NO_OPTIONS}
				value={filters.hasWebsiteProfile}
				isBoolean
				onChange={onChange}
			/>
			<FilterSelect
				heading="Kundentyp"
				paramKey="customerType"
				options={filterOptions(vocabulary, 'customerType')}
				value={filters.customerType}
				onChange={onChange}
			/>
			<FilterSelect
				heading="Geschäftsmodell"
				paramKey="businessModel"
				options={filterOptions(vocabulary, 'businessModel')}
				value={filters.businessModel}
				onChange={onChange}
			/>
			<FilterSelect
				heading="Stellt ein"
				paramKey="hiringActive"
				options={YES_NO_OPTIONS}
				value={filters.hiringActive}
				isBoolean
				onChange={onChange}
			/>
			<FilterSelect
				heading="Alter ältester GF"
				paramKey="oldestCeoAge"
				options={filterOptions(vocabulary, 'oldestCeoAge')}
				value={filters.oldestCeoAge}
				onChange={onChange}
			/>
		</div>
	</HeaderFilter>
);

ProfileFilterControl.propTypes = {
	filters: PropTypes.object.isRequired,
	onChange: PropTypes.func.isRequired,
	vocabulary: PropTypes.object,
};

/** Company-kind filter of the name column, and with targeting search the discovery-source filter. */
export const NameFilterControl = ({ filters, onChange, vocabulary, showSource }) => {
	const kindOptions = filterOptions(vocabulary, 'companyKind');
	return (
		<HeaderFilter
			label="Nach Firmenart oder Quelle filtern"
			active={filters.companyKind !== undefined || (showSource && filters.discoverySource !== undefined)}
		>
			<div className="space-y-2">
				{kindOptions.length > 0 && (
					<FilterSelect
						heading="Firmenart"
						paramKey="companyKind"
						options={kindOptions}
						value={filters.companyKind}
						onChange={onChange}
					/>
				)}
				{showSource && (
					<FilterSelect
						heading="Quelle"
						paramKey="discoverySource"
						options={filterOptions(vocabulary, 'discoverySource')}
						value={filters.discoverySource}
						onChange={onChange}
					/>
				)}
			</div>
		</HeaderFilter>
	);
};

NameFilterControl.propTypes = {
	filters: PropTypes.object.isRequired,
	onChange: PropTypes.func.isRequired,
	vocabulary: PropTypes.object,
	showSource: PropTypes.bool,
};
